import { OrganizationUserContext } from "contexts/Organization";
import { useContext, useRef } from "react";

import { hasTaskByType } from "../helpers";
import { OrganizationGamificationTask } from "../schema";
import AddNicheFollowupCard from "./AddNicheFollowupCard";
import CreateNicheTemplateCard from "./CreateNicheTemplateCard";

interface Props {
  numOfCardsToRender: number | null;
}

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

const ExperienceVa = ({ numOfCardsToRender }: Props) => {
  const { gamificationProfile, credits } = useContext(OrganizationUserContext);

  // Snapshot whether the followup task was already completed when the user
  // first loaded this page. If it was, we hide the card; if it gets completed
  // during this session we still show a brief "completed" state until refresh.
  const followupCompletedOnMount = useRef<boolean | null>(null);
  if (followupCompletedOnMount.current === null && gamificationProfile) {
    followupCompletedOnMount.current = hasTaskByType(
      gamificationProfile,
      OrganizationGamificationTask.niche_followups,
    );
  }

  if (!gamificationProfile || numOfCardsToRender === null) {
    return <></>;
  }

  const nicheTemplateCredits = (credits ?? [])
    .filter(
      (c) => c.transactionType === OrganizationGamificationTask.niche_templates,
    )
    .sort((a, b) => (a.createdAt ?? 0) - (b.createdAt ?? 0));

  const now = Date.now();
  const isCreditStale = (createdAtSeconds: number | undefined): boolean => {
    if (!createdAtSeconds) return false;
    return now - createdAtSeconds * 1000 > ONE_DAY_MS;
  };

  const nicheFollowupCompleted = hasTaskByType(
    gamificationProfile,
    OrganizationGamificationTask.niche_followups,
  );
  const showNicheFollowupCard =
    !followupCompletedOnMount.current &&
    (nicheFollowupCompleted ||
      Boolean(gamificationProfile.vaHasNicheWithoutFollowup));

  return (
    <>
      {Array.from({ length: numOfCardsToRender }).map((_, index) => {
        const credit = nicheTemplateCredits[index];
        if (credit && isCreditStale(credit.createdAt)) {
          return null;
        }
        return (
          <CreateNicheTemplateCard
            key={index}
            title={
              numOfCardsToRender <= 1
                ? `Niche Template`
                : `Niche Template #${index + 1}`
            }
            description={
              index === 0
                ? "Create a pitch template for a specific niche (e.g., beauty, fitness, tech)"
                : "Create a pitch template for a second niche to diversify your opportunities"
            }
            completed={Boolean(credit)}
            infoBox={
              <>
                {index === 0 && (
                  <>
                    <strong>Pro tip:</strong> Different niches respond to
                    different pitching styles. A beauty brand cares about
                    aesthetics and engagement, while a tech brand wants to see
                    expertise and audience demographics.
                  </>
                )}
                {index === 1 && (
                  <>
                    <strong>Why a second niche?</strong> Having templates for
                    multiple niches increases your opportunities and helps you
                    understand which brand categories respond best to your
                    content style.
                  </>
                )}
              </>
            }
          />
        );
      })}

      {showNicheFollowupCard && (
        <AddNicheFollowupCard completed={nicheFollowupCompleted} />
      )}
    </>
  );
};
export default ExperienceVa;
